import { Component, computed, effect, inject, OnDestroy } from '@angular/core';

import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { FormFileUploadComponent, FormInputComponent, FormNumberComponent, FormWysiwygComponent } from '@shared/forms';
import { UnavailableComponent } from '@shared/components';
import { MediaFacade } from '@app/store/media';
import { getMediaTypeAccept } from '@app/store/shared/models';
import { MediaFormService } from '../media-form.service';

@Component({
    selector: 'app-media-edit-modal',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.css'],
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        ButtonModule,
        FormInputComponent,
        FormFileUploadComponent,
        FormNumberComponent,
        FormWysiwygComponent,
        UnavailableComponent,
    ],
    providers: [MediaFormService],
})
export class EditComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly mediaFacade = inject(MediaFacade);
    private readonly mediaFormService = inject(MediaFormService);
    private readonly translateService = inject(TranslateService);

    private readonly mediaId = this.route.parent?.snapshot.params['id'] ?? '';

    readonly media = this.mediaFacade.selectMedia(this.mediaId);
    readonly loading = this.mediaFacade.selectLoading(this.mediaId);
    readonly saving = this.mediaFacade.saving;
    readonly error = this.mediaFacade.selectError(this.mediaId);
    readonly form = this.mediaFormService.form;
    readonly type = computed(() => this.media()?.type ?? null);
    readonly fileAccept = computed(() => getMediaTypeAccept(this.type()));
    readonly title = computed(() => {
        const type = this.type();
        if (!type) return this.translateService.instant('media.modal.edit.title');
        const typeLabel = this.translateService.instant(`media.type.${type}`);
        return this.translateService.instant('media.modal.edit.typeTitle', { type: typeLabel });
    });

    constructor() {
        if (this.mediaId) {
            this.mediaFacade.getMedia(this.mediaId);
        }

        effect(() => {
            const media = this.media();
            if (!media) return;

            this.form.patchValue({
                name: media.name,
                type: media.type,
                mediaId: media.id,
                content: media.content ?? null,
                duration: media.duration ?? null,
            });
        });

        effect(() => {
            if (this.mediaFacade.saved()) {
                this.close();
            }
        });

        effect(() => {
            if (this.mediaFacade.saving() === false && this.mediaFacade.saved() === false) {
                this.form.enable();
                this.mediaFormService.onTypeChange(this.form.controls.type.value);
            }
        });
    }

    ngOnDestroy(): void {
        this.mediaFacade.clear();
    }

    onSubmit(): void {
        const value = this.form.getRawValue();
        this.form.markAllAsTouched();

        const type = this.type();
        if (this.form.invalid || !type) {
            return;
        }

        const media = this.media();
        this.form.disable();
        this.mediaFacade.updateMedia(this.mediaId, {
            name: value.name,
            type,
            mediaId: value.mediaId || undefined,
            originalMediaId: media?.id,
            content: value.content ?? undefined,
            duration: value.duration ?? undefined,
        });
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }]);
    }
}
